import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { Link } from '@ipfabric/ui-components/Link/Link';
import {
  type NodeSn,
  type SnapshotId,
  type TableResponseBody,
  createTextColumn,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { ButtonAnchor } from 'components/Button/ButtonAnchor';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import type { RowDetailData } from 'components/Table/components/TableRowDetail/useRowDetailData';
import type { TableCellContext } from 'components/Table/model/TableCellContext';
import { possibleToConnectProtocols } from 'utils/constants';
import type { ApiHook } from 'utils/request/useApi';

export type InterfaceDetailRow = {
  hostname: string;
  l1: string;
  l2: string;
  reason: string;
  dscr: string;
  mac: string;
  duplex: string;
  speed: string;
  bandwidth: number;
  media: string;
  errDisabled: string;
  sn: NodeSn;
  loginIp: string;
  loginType: string;
  primaryIp: string;
};

export const interfaceDetailColumns = [
  createFrontendOnlyColumn({
    key: 'hostname',
    title: 'Hostname',
    render: (row: InterfaceDetailRow, context: TableCellContext) => (
      <>
        <div>
          <Link
            renderAs='button'
            onClick={() => {
              context.onOpenExplorer!(row, {
                sources: [
                  {
                    hostname: row.hostname,
                    kind: 'device',
                    sn: row.sn,
                  },
                ],
              });
            }}
          >
            {row.hostname}
          </Link>
        </div>
        {row.loginIp &&
          row.loginType &&
          possibleToConnectProtocols.includes(row.loginType.toLowerCase()) && (
            <ButtonAnchor
              variant='secondary'
              accent='positive'
              size='xxs'
              className='mt-3'
              href={`${row.loginType}://${row.loginIp}`}
            >
              Connect
            </ButtonAnchor>
          )}
      </>
    ),
  }),
  createTextColumn({
    key: 'l1',
    title: 'L1 state',
  }),
  createTextColumn({
    key: 'l2',
    title: 'L2 state',
  }),
  createTextColumn({
    key: 'reason',
    title: 'State Reason',
  }),
  createTextColumn({
    key: 'dscr',
    title: 'Description',
  }),
  createTextColumn({
    key: 'mac',
    title: 'MAC',
  }),
  createTextColumn({
    key: 'duplex',
    title: 'Duplex',
  }),
  createTextColumn({
    key: 'speed',
    title: 'Speed',
  }),
  createTextColumn({
    key: 'bandwidth',
    title: 'Bandwidth',
  }),
  createTextColumn({
    key: 'media',
    title: 'Media',
  }),
  createTextColumn({
    key: 'errDisabled',
    title: 'Error Reason',
  }),
  createTextColumn({
    key: 'sn',
    title: 'SN',
    hidden: true,
  }),
  createTextColumn({
    key: 'loginIp',
    title: 'Login IP',
    hidden: true,
  }),
  createTextColumn({
    key: 'loginType',
    title: 'Login Type',
    hidden: true,
  }),
  createTextColumn({
    key: 'primaryIp',
    title: 'IP',
  }),
];

export const loadInterfaceDetailData = async (
  api: ApiHook,
  snapshot: SnapshotId,
  intName: string,
  sn: string,
  abortSignal?: AbortSignal,
): Promise<RowDetailData | null> => {
  const columns = interfaceDetailColumns.map(prop('key'));

  const result = await api.abortableRequest<TableResponseBody<unknown>>({
    url: '/tables/inventory/interfaces',
    method: 'POST',
    data: {
      filters: { intName: ['eq', intName], sn: ['eq', sn] },
      columns,
      snapshot,
    },
    abortSignal,
  });

  if (result.aborted) {
    return null;
  }

  const item = (() => {
    if (result.data.data.length === 0) {
      return null;
    }
    if (result.data.data.length > 1) {
      stumble('More than one record received for interface detail data');
      return result.data.data[0];
    }
    return result.data.data[0];
  })();

  return {
    loadingState: 'LOADED',
    item,
    columns: interfaceDetailColumns,
  };
};
